import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { QRCode } from "antd";
import { CopyOutlined, QrcodeOutlined } from "@ant-design/icons";
import CommonStyles from "@components/CommonStyles";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";

interface IProps {
  code: string;
  /** Text size of the label; the icons follow it. */
  size?: "xs" | "sm";
}

/** "E-Voucher: <code>" with copy and QR buttons. Clicks stay here, so a card around it is not opened too. */
export default function VoucherCode({ code, size = "xs" }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isQrOpen, setIsQrOpen] = useState<boolean>(false);

  //! function
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("Copied") + "!" }));
    } catch (error) {}
  };

  //! render
  const iconButton = "h-5 w-5 flex items-center justify-center rounded text-common-primary hover:bg-common-primary100";

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-common-primary font-medium font-['Inter'] ${size === "sm" ? "text-sm" : "text-xs"}`}
      onClick={(event) => event.stopPropagation()}
    >
      <span>{t("booking.visit.voucherCode", { code })}</span>

      <button type="button" aria-label={t("copy")} title={t("copy")} className={iconButton} onClick={copy}>
        <CopyOutlined />
      </button>

      <button
        type="button"
        aria-label={t("booking.visit.viewQr")}
        title={t("booking.visit.viewQr")}
        className={iconButton}
        onClick={() => setIsQrOpen(true)}
      >
        <QrcodeOutlined />
      </button>

      {isQrOpen ? (
        <CommonStyles.ModalConfirmationUI
          open
          toggle={() => setIsQrOpen(false)}
          type="normal"
          hiddenAction
          width={360}
          title="E-Voucher"
          content={
            <div className="flex flex-col items-center gap-3 py-2">
              <QRCode value={code} size={220} bordered={false} />
              <span className="text-common-maintext text-base font-semibold font-['Inter'] break-all text-center">{code}</span>
              <CommonStyles.ButtonUI ghost icon={<CopyOutlined />} onClick={copy}>
                {t("copy")}
              </CommonStyles.ButtonUI>
            </div>
          }
        />
      ) : null}
    </span>
  );
}
